import {ChangeDetectionStrategy, Component, HostBinding, inject, Input} from '@angular/core';
import {MatPaginator, MatPaginatorIntl, PageEvent} from '@angular/material/paginator';
import {Action, Store} from '@ngrx/store';
import {DecimalPipe} from '@angular/common';
import {PaginationSelectors} from '@aviary/store/pagination/pagination.selectors';
import {RouterActions} from '@aviary/store/router/router.actions';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {PageNumberPaginationDirective} from '@aviary/components/table/pagination/pagination-page-number-layout.directive';
import {PAGINATION_STYLE} from '@aviary/injection-tokens/app-provided.tokens';
import {PaginationStyle} from '@aviary/components/table/table.component.types';

class CustomPaginator extends MatPaginatorIntl {
  constructor(readonly decimalPipe: DecimalPipe) {
    super();
    this.itemsPerPageLabel = 'Rows per page:';

    const originalGetRangeLabel: typeof this.getRangeLabel = this.getRangeLabel;

    this.getRangeLabel =
      (page: number, pageSize: number, length: number) =>
        originalGetRangeLabel(page, pageSize, length)
          .replace(/ [-\u2012\u2013\u2014\u2015] /, '-') // Replace any em-dash, en-dash, etc. in the original label and remove surrounding spaces.
          .replace(/of.*$/, `of ${decimalPipe.transform(length)}`); // Use Angular "number" pipe to format the length with commas.
  }
}

@Component({
  selector: 'mx-pagination',
  template: `
    @let pageSizeOptions = [5, 10, 20, 50, 100];
    @let pageIndex = selectors.zeroBasedPage | select:0;
    @let pageSize = selectors.pageSize | select:0;
    @let length = selectors.totalCount | select:0;
    @let selectConfig = { panelClass: 'mx-pagination' };
    
    <!-- Add a directive to the paginator if we need to render page numbers for this app.  Duplicated because Angular doesn't allow dynamic directives. :( -->
    @if (appPaginationStyle === 'PAGE_NUMBERS') {
      <mat-paginator mxPageNumberPagination [pageSizeOptions]="pageSizeOptions" [pageIndex]="pageIndex" [pageSize]="pageSize" [length]="length" 
                     [selectConfig]="selectConfig" (page)="onPageEvent($event)"/>
    }
    @else {
      <mat-paginator [pageSizeOptions]="pageSizeOptions" [pageIndex]="pageIndex" [pageSize]="pageSize" [length]="length"
                     [selectConfig]="selectConfig" (page)="onPageEvent($event)"/>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DecimalPipe,
    {provide: MatPaginatorIntl, deps: [DecimalPipe], useFactory: (decimalPipe: DecimalPipe) => new CustomPaginator(decimalPipe)}
  ],
  imports: [MatPaginator, SelectPipe, PageNumberPaginationDirective]
})
export class PaginationComponent {
  @Input() updateAction?: ((page: number, pageSize: number) => Action);

  @Input() set itemsPerPageLabel(itemsPerPageLabel: string) {
    this.matPaginator.itemsPerPageLabel = itemsPerPageLabel;
    this.matPaginator.changes.next();
  }

  @HostBinding('class.loaded') public loaded: boolean = true;

  public readonly selectors = PaginationSelectors;

  private readonly store: Store = inject(Store);
  private readonly matPaginator: MatPaginatorIntl = inject(MatPaginatorIntl);
  public readonly appPaginationStyle: PaginationStyle = inject(PAGINATION_STYLE);

  public onPageEvent(event: PageEvent): void {
    this.loaded = false;
    document.body.focus();
    const action = this.updateAction ?? RouterActions.updatePaginationParams;
    // MatPaginator is 0-based, but we are 1-based.
    this.store.dispatch(action(event.pageIndex + 1, event.pageSize));
  }
}
